/* eslint-disable no-console */
import Pubnub from 'pubnub';
import {
  getCallAvailabilityTypeByMajorRank,
  advisorIsBusy,
  advisorIsAway
} from 'services/AvailabilityService';
import { useGetChannelDetailsForAdvisorsMutation } from 'store/api';
import { useAsyncEffect } from 'use-async-effect';

export declare type AdvisorStatusInfo = {
  advisorId: string;
  callAvailabilityId: number;
  callMajorRank: number;
  chatAvailabilityId: number;
  advisorIsBusy: boolean;
  advisorIsAway: boolean;
};

type initializationCallback = (client: Pubnub) => any;
type availabilityChangeCallback = (statusInfo: AdvisorStatusInfo) => any;
type onErrorCallback = (error: any) => any;

export const useAdvisorAvailability = (
  advisorIds = [],
  onInit: initializationCallback = () => {},
  onAvailabilityChange: availabilityChangeCallback = () => {},
  onError: onErrorCallback = () => {}
) => {
  const [getAdvisorChannelDetails] = useGetChannelDetailsForAdvisorsMutation();

  const initialize = async () => {
    if (advisorIds.length === 0) {
      return { client: null, channels: null };
    }

    try {
      const settings = await getAdvisorChannelDetails(advisorIds).unwrap();
      const client = new Pubnub({
        authKey: settings.authKey,
        publishKey: settings.publishKey,
        subscribeKey: settings.subscribeKey,
        ssl: true,
        userId: '0'
      });

      const channels = settings.channelNames;
      client.subscribe({ channels });
      console.info(`AdvisorAvailability > Subscribe : ${channels}`);

      const handleMessage = (data, onMessageCallback) => {
        // console.log(JSON.stringify(data));
        const { message } = data;
        // the constants used on the UI for call availability is a simplified version of the backend.
        // since the UI expects these constants, we need to manually translate.
        const callAvailabilityId = getCallAvailabilityTypeByMajorRank(
          message.CallMajorRank
        );

        const status: AdvisorStatusInfo = {
          advisorId: message.AdvisorId,
          callAvailabilityId,
          callMajorRank: message.CallMajorRank,
          chatAvailabilityId: message.ChatUserStatus,

          advisorIsBusy: advisorIsBusy(
            callAvailabilityId,
            message.ChatUserStatus
          ),
          advisorIsAway: advisorIsAway(
            callAvailabilityId,
            message.ChatUserStatus
          )
        };

        if (onMessageCallback) {
          onMessageCallback(status);
        }
      };
      client.addListener({
        message: (m) => handleMessage(m, onAvailabilityChange)
      });
      onInit(client);
      return { client, channels };
    } catch (error) {
      onError(error);
      return { client: null, channels: null };
    }
  };

  const cleanup = ({ client = null, channels = null } = {}) => {
    if (client && channels) {
      client.unsubscribe({ channel: channels });
      console.info(`AdvisorAvailability > Unsubscribe : ${channels}`);
    }
  };

  useAsyncEffect(initialize, cleanup, [JSON.stringify([...advisorIds].sort())]);
};
